import { useEffect, useState } from 'react';
import { useAuthenticationProvider } from '../../authentication/authProvider';
import { useFetchOrganisationList } from '../../hooks/useFetchOrganisationList';
import { Building, Logout } from '../icons';
import { useNavigate } from 'react-router-dom';
import { useFetchUser } from '../../hooks/useFetchUser';
import SelectionList from './selectionlist/SelectionList';
import OnboardContent from './OnboardContent';
import { LOCAL_STORAGE_NAMES, ROUTES, UserType } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../confirmationmodal/confirmationModal';
import { useUser } from '../../context/UserContext';
import { IAccountProfile, IUser, IUserDetails } from '../../interface/user';
import { Button, Spin } from '../Ant';
import LoggedUser from './loggedUser';
import { Flex } from 'antd';
import { useGenerateCSRFToken } from '../../hooks/useGenerateCSRFToken';
import { useFetchAccountProfile } from '../../hooks/useFetchAccountProfile';
import { hasCompletedWorkspaceSetup, readStartModuleForUser } from '../../context/FeatureContext';
import { getRouteAfterWorkspaceSetup } from '../../utils/startModule';

const OrganisationSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateUser, updateUserDetails } = useUser();
  const { data: userProfile } = useFetchAccountProfile();
  const { data = [], error, isLoading } = useFetchOrganisationList();
  const { logout } = useAuthenticationProvider();
  const { mutateAsync: generateCSRFToken } = useGenerateCSRFToken();
  const { refetch } = useFetchUser({
    relations: ['userRole'],
  });
  const [selectOrganisation, setSelectOrganisation] = useState<IOrganisation | null>(null);
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState<boolean>(false);

  const requestCsrfToken = async () => {
    if (Office.context.diagnostics.platform === Office.PlatformType.Mac) return;
    const response = await generateCSRFToken(undefined);
    if (response && window?.location?.hostname === 'localhost') {
      localStorage.setItem(LOCAL_STORAGE_NAMES.csrfToken, response?.csrfToken);
    }
  };

  const loadUserForOrganisation = async () => {
    const [, userResult] = await Promise.all([requestCsrfToken(), refetch()]);
    if (!userResult.data) throw userResult.error ?? new Error('User details unavailable');
    return userResult.data as IUserDetails & IUser;
  };

  /** Workspace setup is shown once per user; after that, logging in goes straight to their start module. */
  const navigateAfterLogin = (user: IUser) => {
    if (!hasCompletedWorkspaceSetup(user.id)) {
      sessionStorage.setItem(LOCAL_STORAGE_NAMES.isOnboarding, 'true');
      navigate(ROUTES.FEATURE_SELECTION);
      return;
    }
    const isRestricted = user.userType === UserType.RESTRICTED;
    const startModule = readStartModuleForUser(user.id, isRestricted);
    navigate(getRouteAfterWorkspaceSetup(startModule, user.id));
  };

  const handleSelectOrganisation = async (currentOrganisation?: IOrganisation) => {
    const selectedOrganisation = currentOrganisation ?? selectOrganisation;
    sessionStorage.setItem(LOCAL_STORAGE_NAMES.isSelectingOrganisation, 'true');
    localStorage.setItem(
      LOCAL_STORAGE_NAMES.currentOrganisation,
      JSON.stringify(selectedOrganisation),
    );
    try {
      const user = await loadUserForOrganisation();
      updateUserDetails(user);
      updateUser({
        email: user.email,
        fullName: user.fullName,
        id: user.id,
        userType: user.userType,
        status: user.status,
        userRole: {
          hidePricingInformation: user.userRole?.hidePricingInformation ?? false,
        },
      });
      navigateAfterLogin(user);
    } catch {
      // the axios interceptor already routes the user to the matching error page
    }
  };

  const handleLogout = () => {
    logout();
    setIsLogoutModalVisible(false);
  };
  useEffect(() => {
    if (data?.length === 1 && data[0]?.isOperational) {
      setSelectOrganisation(data[0]);
      void handleSelectOrganisation(data[0]);
    }
  }, [data]);
  // Cleared only once the login has navigated away, since MainLayout can sign the user in before that.
  useEffect(() => () => sessionStorage.removeItem(LOCAL_STORAGE_NAMES.isSelectingOrganisation), []);

  if (isLoading || data?.length === 1) return <Spin fullscreen data-testid="loading-spinner" />;
  if (error) {
    return <div role="alert">{error.message}</div>;
  }

  return (
    <Flex vertical gap={15} className="h-full pb-gutter">
      <LoggedUser user={userProfile as IAccountProfile} />
      {data.length > 0 && (
        <SelectionList
          organisationList={data}
          selectOrganisation={selectOrganisation ?? undefined}
          handleSelectOrganisation={handleSelectOrganisation}
          setSelectOrganisation={setSelectOrganisation}
        />
      )}
      {data.length === 0 && (
        <Flex vertical justify="center" align="center" className="h-full w-full">
          <OnboardContent
            data-testid="no-organisation-onboard-content"
            icon={<Building className="building-icon" />}
            title={t('common:noOrganizations')}
            subtitle={t('common:noOrganizationConnected')}
          />
          <Button
            data-testid="no-organisation-logout-button"
            onClick={() => setIsLogoutModalVisible(true)}
            size="large"
            block
            className="logout-btn"
            icon={<Logout />}
          >
            {t('common:logout')}
          </Button>
        </Flex>
      )}
      <ConfirmModal
        visible={isLogoutModalVisible}
        onClose={() => setIsLogoutModalVisible(false)}
        icon={<Logout />}
        title={t('common:logOutQuestion')}
        subtitle={t('common:logOutMessage')}
        cancelText={t('common:cancel')}
        confirmText={t('common:logOutBtnText')}
        onConfirm={handleLogout}
      />
    </Flex>
  );
};

export default OrganisationSelection;
